import React, { useCallback, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DefaultButton, DirectionalHint, TooltipHost } from '@fluentui/react';
import { siteMapPropType, withSiteMap, findSiteMap, getSiteMapItemRoute, processorSet } from '@bingads-webui-campaign-react/with-site-map';
import { Link } from 'react-router-dom';
import { UserContext } from '@bingads-webui-react/user-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useMultiSelectContext } from '@bingads-webui-campaign-react/multi-selector';
import { InlineLoading } from '@bingads-webui-campaign-react/loading';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { ManageBrandListPanel } from '../manage-brand-list';
import { ExistingBrandListsPicker } from './existing-brand-lists-picker';
import { SelectedBrandListsTags } from './selected-brand-lists-tags';
import { MaxSelectionAlertForBrandLists, NoInitialDataAlertForBrandLists, SystemErrorAlertForBrandLists } from '../alerts';
import { ABOUT_BRAND_LISTS_LINK, BRAND_LIST_MAX_COUNT } from '../constants';

const getStyles = () => ({
  BrandListsPicker: {
    '& .ms-MessageBar': {
      width: '745px',
      marginBottom: '12px',
    },
    '& .inline-loading': {
      width: 'fit-content',
    },
  },
  BrandListsPickerOperations: {
    display: 'flex',
    gap: '8px',
    width: 'max-content',
  },
  Description: {
    marginBottom: 8,
  },
});

const BrandListsPickerContentImpl = ({
  cid,
  aid,
  brandListId,
  className,
  disabled,
  isConfirmDialogOn,
  isBrandListLimitReached,
  descriptionTextTemplate,
  noInitialDataText,
  unscopedSiteMap,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);

  const {
    triggerLoadOptions,
    selectedOptions,
    setSelectedOptions,
    selectedOptionsLimit,
    isLoadingOptions,
    isLoadingSelectedOptions,
    isSelectedOptionsLimitReached,
    isFirstOptionsLoad,
    isNoOptionsAfterFirstLoad,
    optionsLoadError,
    selectedOptionsLoadError,
  } = useMultiSelectContext();

  const isAnyLoading = isLoadingOptions || isLoadingSelectedOptions;
  const hasLoadErrors = optionsLoadError || selectedOptionsLoadError;
  const showInitialLoading = (isFirstOptionsLoad && !hasLoadErrors) || isLoadingSelectedOptions;
  const hasInitialLoadErrors = isFirstOptionsLoad && hasLoadErrors;
  const canUserStartOperating = !showInitialLoading && !(hasInitialLoadErrors || selectedOptionsLoadError);

  const onSave = useCallback(({
    listId, listName,
  }) => {
    // refresh options
    triggerLoadOptions();
    // add the newly created brand list to selected options
    const newSelectedOptions = _.clone(selectedOptions);
    newSelectedOptions.push({
      key: Number(listId),
      Id: listId,
      Name: listName,
    });
    setSelectedOptions(newSelectedOptions);
  }, [selectedOptions, setSelectedOptions, triggerLoadOptions]);

  const userContext = useContext(UserContext);
  const { scope } = useContext(ScopeContext);
  const brandListsPageLink = useMemo(() => {
    const siteMapItemId = 'brand-lists';
    const siteMapItem = findSiteMap(unscopedSiteMap, item => item.id === siteMapItemId);
    return getSiteMapItemRoute({ item: siteMapItem, scope, userContext }).href;
  }, [unscopedSiteMap, userContext, scope]);
  return (
    <div className={classNames(className, 'brand-lists-picker', classes.BrandListsPicker, disabled ? 'disabled' : '')}>
      {descriptionTextTemplate &&
        <div className={classNames('brand-lists-picker-description', classes.Description)}>
          <ReactTemplate
            model={{
              link: text => (<Link to={brandListsPageLink}>{text}</Link>),
              aboutLink: text => (<a href={ABOUT_BRAND_LISTS_LINK} target="_blank" rel="noopener noreferrer">{text}</a>),
              count: BRAND_LIST_MAX_COUNT,
            }}
            template={descriptionTextTemplate}
          />
        </div>
      }
      {isAnyLoading && <InlineLoading text={i18n.getString(_TL_('Loading...'))} />}
      {!showInitialLoading && isNoOptionsAfterFirstLoad && <NoInitialDataAlertForBrandLists noInitialDataText={noInitialDataText} />}
      {!isAnyLoading && hasLoadErrors && <SystemErrorAlertForBrandLists />}
      {!showInitialLoading && isSelectedOptionsLimitReached && <MaxSelectionAlertForBrandLists selectedOptionsLimit={selectedOptionsLimit} />}
      {canUserStartOperating && selectedOptions && selectedOptions.length > 0 && (
        <SelectedBrandListsTags
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          isDisabled={disabled}
        />
      )}
      {canUserStartOperating && (
        <div className={classNames('brand-lists-picker-operations', classes.BrandListsPickerOperations)}>
          <ExistingBrandListsPicker disabled={disabled} />
          {isBrandListLimitReached ? (
            <TooltipHost
              directionalHint={DirectionalHint.rightCenter}
              content={i18n.getString(_TL_('You have reached the max number of lists that you can create. Manage your lists at Brand lists. '))}
              calloutProps={{ gapSpace: 0 }}
            >
              <DefaultButton
                className={classNames('create-button', classes.CreateButton)}
                text={i18n.getString(_TL_('Create new brand list'))}
                disabled
              />
            </TooltipHost>
          ) : (
            <DefaultButton
              className={classNames('create-button', classes.CreateButton)}
              text={i18n.getString(_TL_('Create new brand list'))}
              onClick={() => setIsCreatePanelOpen(true)}
              disabled={disabled || isAnyLoading || isSelectedOptionsLimitReached}
            />
          )}
        </div>
      )}
      <ManageBrandListPanel
        cid={cid}
        aid={aid}
        brandListId={brandListId}
        className="create-brand-list-panel"
        isOpen={isCreatePanelOpen}
        onDismiss={() => setIsCreatePanelOpen(false)}
        onSaveCallback={onSave}
        isConfirmDialogOn={isConfirmDialogOn}
      />
    </div>
  );
};

BrandListsPickerContentImpl.propTypes = {
  unscopedSiteMap: siteMapPropType.isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  brandListId: PropTypes.number,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isConfirmDialogOn: PropTypes.bool,
  isBrandListLimitReached: PropTypes.bool.isRequired,
  descriptionTextTemplate: PropTypes.string,
  noInitialDataText: PropTypes.string,
};

BrandListsPickerContentImpl.defaultProps = {
  brandListId: undefined,
  className: undefined,
  disabled: false,
  isConfirmDialogOn: false,
  descriptionTextTemplate: '',
  noInitialDataText: undefined,
};

const scopeUnawareWithSiteMap = _.partial(withSiteMap, _, processorSet.basic, _, _, 'unscopedSiteMap');
export const BrandListsPickerContent = scopeUnawareWithSiteMap(BrandListsPickerContentImpl);
